import * as React from 'react';
import { IOpactiyPickerProps } from './spectrum-picker-props';
import { useDrag, IConstrainedDragInfo } from '../../../hooks';
import styled from 'styled-components';
import { knobStyles, pickerSurfaceStyles } from './styles';
import { selectThemeSemantics } from '../../../styling';

const checkedBackground = 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAJUlEQVQYV2N89erVfwY0ICYmxoguxjgUFKI7GsTH5m4M3w1ChQC1/Ca8i2n1WgAAAABJRU5ErkJggg==")';

const SpectrumSurface = styled.div.attrs((props: Pick<IOpactiyPickerProps, 'dimensionSize'>) => props)`
    background-image: ${checkedBackground};
    position: relative;
    box-sizing: border-box;
    margin-left: ${selectThemeSemantics(({ gap }) => gap.xxl)};
    display: inline-block;
    width: 25px;
    height: ${props => `${props.dimensionSize}px`};
    ${pickerSurfaceStyles}
`;

interface IOpactiySpectrumProps extends Pick<IOpactiyPickerProps, 'pointerSize'> {
    background: string;
    dragRef: React.RefObject<HTMLDivElement>;
    top: number;
}

const Spectrum = styled.div.attrs((props: Pick<IOpactiySpectrumProps, 'background'>) => props)`
    box-sizing: border-box;
    position: relative;
    display: inline-block;
    cursor: pointer;
    width: 24px;
    height: 100%;
    background: ${({ background }) => background};
`;

type DragProps = Pick<IOpactiySpectrumProps, 'pointerSize' | 'top'>;
const Drag = styled.div.attrs(({ top }: DragProps) => ({
    style: { top: `${top}%` }
})) <DragProps>`
    position: absolute;
    transform: translate(0px, -50%);
    user-select: none;
    left: 0;
    width: ${props => `${props.pointerSize}px`};
    height: ${props => `${props.pointerSize}px`};
    ${knobStyles}
`;

function OpacitySpectrum(props: IOpactiySpectrumProps) {
    return (
        <Spectrum background={props.background}>
            <Drag top={props.top} pointerSize={props.pointerSize} ref={props.dragRef} />
        </Spectrum>
    );
}

const OpacityPickerBase = (props: IOpactiyPickerProps) => {
    const surfaceRef = React.useRef<HTMLDivElement>(null);
    const dragThumbRef = React.useRef<HTMLDivElement>(null);
    const opacityValue = React.useRef(props.value);

    const headDragY = props.value;

    const handleDragging = React.useCallback((currentDragInfo: IConstrainedDragInfo) => {
        setDragInfo(currentDragInfo);
        const oldOpacityValue = opacityValue.current;
        const currentOpacityValue = currentDragInfo.top;
        if (oldOpacityValue === currentOpacityValue) {
            return;
        }

        opacityValue.current = currentOpacityValue;

        if (props.onValueChange === undefined) {
            return;
        }

        props.onValueChange(currentOpacityValue);
    }, [props]);

    const [initialDragInfo, setDragPosition] = useDrag({
        constraintsTo: surfaceRef,
        target: dragThumbRef,
        dragBufferX: (props.pointerSize! / 2),
        headDragX: 0,
        headDragY,
        unit: '%',
        onDragging: handleDragging
    });

    const [dragInfo, setDragInfo] = React.useState<IConstrainedDragInfo>(initialDragInfo);

    React.useEffect(() => {
        setDragPosition(0, props.value);
        setDragInfo({ left: 0, top: props.value });
    }, [props.value, props.dimensionSize, setDragPosition]);

    const spectrumBackground = `linear-gradient(to bottom, transparent 0px, ${props.baseColor} 100%)`;
    return (
        <SpectrumSurface ref={surfaceRef} className={props.className} dimensionSize={props.dimensionSize}>
            <OpacitySpectrum dragRef={dragThumbRef} background={spectrumBackground} pointerSize={props.pointerSize} top={dragInfo.top} />
        </SpectrumSurface>
    );
};

OpacityPickerBase.defaultProps = {
    dimensionSize: 220,
    pointerSize: 24
};
OpacityPickerBase.displayName = 'OpacityPicker';
export const OpacityPicker = React.memo(OpacityPickerBase);
